import { Modal, Spin, Tag, Button, InputNumber, message } from "antd";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { svg } from "../../../../Assets/svg";
import { useAppDispatch } from "../../../../store";
import { getAllVariantListAPI, getProductsListAPI } from "../../Products/productApis";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounce, getThumbnailUrl } from "../../../../helpers/utils";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { SyncOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { getProductTypeListAPI } from "../productTypeApis";
import { BtnLoader } from "../../../../Components/Components";
import BOMProductConfigurator from "./BOMProductConfigurator";
import DescriptionWithShowMore from "../../../../helpers/DescriptionWithShowMore";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  onSelectionChange: (items: any[]) => void;
  onGenerateBOM?: (items: any[]) => void;
  isGenerating?: boolean;
}

const VariantPickerModal: React.FC<Props> = ({
  open,
  onClose,
  selectedItems,
  onSelectionChange,
  onGenerateBOM,
  isGenerating = false,
}) => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [internalSelectedItems, setInternalSelectedItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showVariants, setShowVariants] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const limit = 20;
  
  const tagsRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const [dynamicCategories, setDynamicCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [productTypesLoading, setProductTypesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const debouncedSearch = useDebounce(search, 500);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchProductTypes = () => {
    setProductTypesLoading(true);
    dispatch(
      getProductTypeListAPI({
        filters: { limit: 100 },
        onSuccess: (data: any) => {
          const list = data?.data ? data.data : (Array.isArray(data) ? data : []);
          setDynamicCategories(
            list.map((item: any) => ({
              label: item?.name,
              value: item?.slug || item?._id, // fallback to id if slug not available
            }))
          );
          setProductTypesLoading(false);
        },
        onError: () => {
          setProductTypesLoading(false);
        },
      })
    );
  };

  useEffect(() => {
    if (open) {
      setInternalSelectedItems(selectedItems);
      setSearch("");
      setShowVariants(false);
      setExpandedProductId(null);
      setSelectedCategory("All");
      setPage(1);
      fetchProductTypes();
    }
  }, [open]);

  // Sync scrollability
  useEffect(() => {
    const el = tagsRef.current;
    if (!el || productTypesLoading || !open) {
      setIsScrollable(false);
      return;
    }

    const checkScrollable = () => {
      if (tagsRef.current) {
        setIsScrollable(
          tagsRef.current.scrollWidth > tagsRef.current.clientWidth + 1
        );
      }
    };

    const resizeObserver = new ResizeObserver(() => checkScrollable());
    resizeObserver.observe(el);
    window.addEventListener("resize", checkScrollable);
    const timer = setTimeout(checkScrollable, 50);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkScrollable);
      clearTimeout(timer);
    };
  }, [productTypesLoading, dynamicCategories, open]);

  const scrollTags = (direction: "left" | "right") => {
    if (tagsRef.current) {
      const scrollAmount = 200;
      tagsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Fetch variants data
  useEffect(() => {
    if (open && !productTypesLoading) {
      setItems([]);
      setPage(1);
      setHasMore(true);
      fetchData(1, true, debouncedSearch, showVariants);
    }
  }, [open, debouncedSearch, selectedCategory, productTypesLoading, showVariants]);

  const fetchData = (pageNum: number, isReset: boolean, searchQuery: string, isVariants: boolean) => {
    if (!open) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    const apiCall = isVariants ? getAllVariantListAPI : getProductsListAPI;
    const filters = isVariants
      ? {
          page: pageNum,
          limit: limit,
          search: searchQuery,
          // type: "product",
          component_type: selectedCategory !== "All" ? selectedCategory : undefined,
        } as any
      : {
          page: pageNum,
          limit: limit,
          // product_type: "product",
          search: searchQuery,
          component_type: selectedCategory !== "All" ? selectedCategory : undefined,
          variant_check: true,
        } as any;

    dispatch(
      apiCall({
        filters,
        onSuccess: (data: any) => {
          const newData = isVariants ? (data?.variants || []) : (data?.products || []);
          const totalCount = data?.total || data?.pagination?.total || 0;
          setTotal(totalCount);

          if (isReset) {
            setItems(newData);
          } else {
            setItems((prev) => {
              const currentIds = new Set(prev.map((i: any) => String(i._id)));
              const uniqueNew = newData.filter(
                (i: any) => !currentIds.has(String(i._id))
              );
              return [...prev, ...uniqueNew];
            });
          }
          setHasMore(pageNum * limit < totalCount);
          setLoading(false);
        },
        onError: () => setLoading(false),
        signal: controller.signal,
      })
    );
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(nextPage, false, search, showVariants);
  };

  const categories = useMemo(
    () => [{ value: "All", label: "All" }, ...dynamicCategories],
    [dynamicCategories]
  );

  const getVariantQty = (variantId: string) => {
    const item = internalSelectedItems.find((i) => i.variant_id === variantId);
    return item ? item.qty : 0;
  };

  // Count items by category for badges
  const variantCounts = useMemo(() => {
    const counts: Record<string, number> = { All: 0 };
    internalSelectedItems.forEach((item) => {
      counts["All"] += 1;
      let rawType = item.variantData?.product?.component_type || item.variantData?.component_type;
      const type = typeof rawType === "object" && rawType !== null ? (rawType.slug || rawType.name) : rawType;
      if (type) {
        counts[type] = (counts[type] || 0) + 1;
      }
    });
    return counts;
  }, [internalSelectedItems]);

  const handleVariantQtyChange = (variant: any, newQty: number) => {
    const targetId = variant._id || variant.variant_id;
    const existingIndex = internalSelectedItems.findIndex(
      (item) => item.variant_id === targetId
    );

    const updatedItems = [...internalSelectedItems];

    if (newQty <= 0) {
      if (existingIndex !== -1) {
        updatedItems.splice(existingIndex, 1);
      }
    } else {
      if (existingIndex !== -1) {
        updatedItems[existingIndex] = { ...updatedItems[existingIndex], qty: newQty };
      } else {
        updatedItems.push({
          variant_id: targetId,
          qty: newQty,
          variantData: variant,
        });
      }
    }

    setInternalSelectedItems(updatedItems);
  };

  const displayItems = useMemo(() => {
    if (search.trim() !== "" || !showVariants) {
      return items;
    }
    
    const selectedIds = new Set(internalSelectedItems.map((s) => String(s.variant_id)));
    const preSelected = internalSelectedItems.map((s) => s.variantData).filter(Boolean);
    const unselectedItems = items.filter((item: any) => !selectedIds.has(String(item._id || item.variant_id)));
    return [...preSelected, ...unselectedItems];
  }, [items, internalSelectedItems, search, showVariants]);

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width="60rem"
      className="commonModalClass accessories-modal addProductModal"
      title={
        <div className="text-center fs-4 fw-bolder">Search {showVariants ? "Variants" : "Products"}</div>
      }
    >
      <div className="d-flex flex-column gap-2 mt-sm-2 h-100">
        <div className="d-flex gap-2 align-items-center mb-2">
          <div className="flex-grow-1 position-relative">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder={`Search ${showVariants ? "Variants" : "Products"}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingRight: "8.5rem" }}
            />
            <button
              onClick={() => {
                setShowVariants(!showVariants);
                setExpandedProductId(null);
              }}
              className="btn btn-light btn-lg border d-flex align-items-center justify-content-center gap-2 px-3 text-nowrap position-absolute top-50 end-0 translate-middle-y py-1 me-1 productVariantBtn"
            >
              {showVariants ? "Variants" : "Products"}
              <SyncOutlined spin={loading} style={{ fontSize: "0.875rem" }} />
            </button>
          </div>
        </div>

        {/* Horizontal Tags Slider */}
        {!productTypesLoading && (
          <div className="position-relative d-flex align-items-center mb-1">
            {isScrollable && (
              <Button
                type="text"
                size="small"
                icon={svg?.leftBtnArrow}
                onClick={() => scrollTags("left")}
                className="flex-shrink-0 me-2 leftrightArrowBtn svg-24"
              />
            )}
            <div
              ref={tagsRef}
              className="d-flex customTag gap-2 overflow-x-auto pb-2 flex-shrink-1 hide-scrollbar"
            >
              {categories.map((cat) => (
                <Tag
                  key={cat.value}
                  className={`rounded-2 cursor-pointer border-0 m-0 ${
                    selectedCategory === cat.value
                      ? "bg-dark text-white selected-tag-item"
                      : "bg-light text-muted"
                  }`}
                  style={{ transition: "all 0.2s" }}
                  onClick={() => {
                    setSearch("");
                    setSelectedCategory(cat.value);
                  }}
                >
                  {cat.label}{" "}
                  <span className="badge customBadge">
                    {variantCounts[cat.value] > 0 &&
                      `${variantCounts[cat.value]}`}
                  </span>
                </Tag>
              ))}
            </div>
            {isScrollable && (
              <Button
                type="text"
                size="small"
                icon={svg?.rightBtnArrow}
                onClick={() => scrollTags("right")}
                className="flex-shrink-0 ms-2 leftrightArrowBtn svg-24"
              />
            )}
          </div>
        )}

        <div
          id="productModalScroll"
          className="pe-2 custom-scrollbar flex-grow-1"
          style={{ overflowY: "auto", maxHeight: "50vh" }}
        >
          {(loading || productTypesLoading) && displayItems.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spin size="large" />
            </div>
          ) : !(loading || productTypesLoading) && displayItems.length === 0 ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 py-5 text-muted">
              {svg?.noQuotationIcon}
              <p className="mt-2 text-center fs-5">No items found</p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={items.length}
              next={handleLoadMore}
              hasMore={hasMore}
              loader={
                <div className="text-center py-2">
                  <Spin />
                </div>
              }
              scrollableTarget="productModalScroll"
            >
              <div className="d-flex flex-column">
              {displayItems.map((item, idx) => {
                const isProductMode = !showVariants && !item.variant_id && !item.variantData;
                const currentQty = getVariantQty(item._id || item.variant_id);
                const productData = item.product_id || item.product || {};
                const title = item.variant_name || productData.name || item.name || "Unnamed Item";
                const imgSrcToUse = item.pre_galvanised_image ||
                      productData.pre_galvanised_image ||
                      productData.hot_dip_galvanised_image ||
                      productData.powder_coated_image;

                if (isProductMode) {
                  return (
                    <div key={idx} className="card p-3 border-0 shadow-sm mb-3">
                      <div className="d-flex gap-3 align-items-center cursor-pointer" onClick={() => setExpandedProductId(expandedProductId === item._id ? null : item._id)}>
                        <img
                          src={imgSrcToUse ? getThumbnailUrl(imgSrcToUse) : placeHolderImage}
                          alt={title}
                          className="object-fit-cover rounded-3 img-60"
                          width={40}
                          height={50}
                        />
                        <div className="flex-grow-1">
                          <h6 className="fw-bold fs-5 mb-0">{title}</h6>
                          {item.description && (
                            <DescriptionWithShowMore description={item.description} />
                          )}
                        </div>
                        <div>
                          <Button type="primary" className="btn-dark px-4" onClick={(e) => {
                            e.stopPropagation();
                            setExpandedProductId(expandedProductId === item._id ? null : item._id);
                          }}>
                            {expandedProductId === item._id ? "COLLAPSE" : "SELECT"}
                          </Button>
                        </div>
                      </div>
                      
                      {expandedProductId === item._id && (
                        <BOMProductConfigurator
                          productItem={item}
                          onCancel={() => setExpandedProductId(null)}
                          onVariantResolved={(variant, qty) => {
                            handleVariantQtyChange(variant, qty);
                            setExpandedProductId(null);
                          }}
                        />
                      )}
                    </div>
                  );
                }

                // Render Variant Line
                return (
                  <div
                    key={idx}
                    className="d-flex gap-sm-3 gap-1 py-2 px-0 border-bottom product-hover-item align-items-center"
                  >
                    <div className="d-flex gap-2 w-100">
                      <img
                        src={imgSrcToUse ? getThumbnailUrl(imgSrcToUse) : placeHolderImage}
                        alt={title}
                        className="img-40 rounded-3 object-fit-cover bg-light border"
                        width={60}
                        height={60}
                        loading="lazy"
                      />
                      <div className="flex-grow-1 d-flex flex-column justify-content-center text-break overflow-hidden">
                        <div>
                          <h6 className="mb-0 fs-6 fw-bold text-dark float-start me-2 mt-1">
                            {title}
                          </h6>
                        </div>
                      </div>
                    </div>
                      
                    {/* Qty Controls */}
                    <div
                      className="ms-sm-auto ms-4 ps-sm-0 ps-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {currentQty === 0 ? (
                        <button
                          className="btn btn-dark btn-sm px-3 fs-6 text-nowrap"
                          style={{ minWidth: "8.5rem" }}
                          onClick={() => handleVariantQtyChange(item, 1)}
                        >
                          + ADD
                        </button>
                      ) : (
                        <div
                          className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput bg-white"
                          style={{ width: "8.5rem" }}
                        >
                          <Button
                            type="text"
                            icon={<MinusOutlined />}
                            className="btn btn-primary addRemoveBtn"
                            style={{
                              backgroundColor: "#212529",
                              color: "white",
                            }}
                            onClick={() => handleVariantQtyChange(item, currentQty - 1)}
                          />
                          <InputNumber
                            min={0}
                            onWheel={(e) => e.currentTarget.blur()}
                            value={currentQty}
                            className="border-0 text-center w-100 shadow-none qnum"
                            controls={false}
                            onChange={(val) => handleVariantQtyChange(item, val || 0)}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            className="btn btn-primary addRemoveBtn"
                            style={{
                              backgroundColor: "#212529",
                              color: "white",
                            }}
                            onClick={() => handleVariantQtyChange(item, currentQty + 1)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </InfiniteScroll>
          )}
        </div>

        {/* Modal Footer */}
        <div className="d-flex justify-content-center pt-3 border-top pb-3">
          <button
            type="button"
            className="btn btn-dark btn-lg fw-bold d-flex align-items-center justify-content-center gap-2 px-4"
            onClick={() => {
              onSelectionChange(internalSelectedItems);
              onGenerateBOM?.(internalSelectedItems);
            }}
            disabled={isGenerating || internalSelectedItems.length === 0}
          >
            {isGenerating && <BtnLoader />}
            <span>GENERATE BOM {internalSelectedItems.length > 0 && `(${internalSelectedItems.length})`}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VariantPickerModal;
